import { isUndefined } from 'lodash';

import { buildCartUrl } from '../../../utils/urls';

export const ZB_MONEY_PRO = 'zb_money_pro';
export const ZB_MONEY_ESSENTIAL = 'zb_money_essential';
export const ZB_MONEY_UNLINKED = 'none';

export const ZB_MONEY_CONTEXT_DEFAULTS = {
  accountTier: ZB_MONEY_UNLINKED,
  isPlaidLinked: false
};

export const SCREEN_BY_ROUTE = {
  'expense-tracker': 'home',
  deductionDashboard: 'deductionFinderLikely',
  expenses: 'expenseDash',
  reports: 'reportDash',
  income: 'incomeDash',
  invoices: 'invoiceDash',
  'time-dash': 'timeDash',
  taxes: 'taxDash',
  tiles: 'tiles',
  mileage: 'mileageDash'
};

export function navigateTo(href) {
  // Navigate parent window from iFrame without
  // appending iFrame history to parent window history
  // for browser back button consistency
  window.top.location.href = href;
}

function navigateToZbPlaidFlow(businessEntityUuid) {
  navigateTo(buildCartUrl('register_zbm_plaid', { businessEntityUuid }));
}

// Related Hurdlr documentation: https://docs.google.com/document/d/1wbtdkIpBF8mRcOoE5NNpezVe8KKDAAMsHv6LCo6WpBE/edit
export function configureZbMoneyTileListenerCallback({ businessEntityUuid, accountTier }) {
  return ({ type }) => {
    //   Freemium
    if (accountTier === ZB_MONEY_ESSENTIAL) {
      switch (type) {
        case 'Hurdlr.EXPENSE_TILE': // route to Expense screen
          navigateTo(`/d/company/${businessEntityUuid}/money/expenses`);
          break;
        case 'Hurdlr.LINK_ACCOUNT': // route to your Plaid Link
          navigateTo(buildCartUrl('register_zbm_plaid', { businessEntityUuid }));
          break;
        case 'Hurdlr.CASH_FLOW_TILE': // route to your upgrade screen
        case 'Hurdlr.INVOICE_TILE': // route to your upgrade screen
        case 'Hurdlr.PROFIT_LOSS_TILE': // route to your upgrade screen
        case 'Hurdlr.REVENUE_TILE': // route to your upgrade screen
        case 'Hurdlr.TAX_TILE': // route to your upgrade screen
        case 'Hurdlr.MILEAGE_TILE': // route to your upgrade screen
        default:
          navigateTo(buildCartUrl('zb_money_pro', { businessEntityUuid, isUpgrade: true }));
          break;
      }
    } else if (accountTier === ZB_MONEY_PRO) {
      // Pro
      switch (type) {
        case 'Hurdlr.EXPENSE_TILE': // route to Expense screen
          navigateTo(`/d/company/${businessEntityUuid}/money/expenses`);
          break;
        case 'Hurdlr.INVOICE_TILE': // route to Invoicing screen
          navigateTo(`/d/company/${businessEntityUuid}/money/invoices`);
          break;
        case 'Hurdlr.LINK_ACCOUNT': // route to your Plaid Link
          navigateToZbPlaidFlow(businessEntityUuid);
          break;
        case 'Hurdlr.REVENUE_TILE': // route to Income screen
          navigateTo(`/d/company/${businessEntityUuid}/money/income`);
          break;
        case 'Hurdlr.TAX_TILE': // route to Taxes screen
          navigateTo(`/d/company/${businessEntityUuid}/money/taxes`);
          break;
        case 'Hurdlr.MILEAGE_TILE': // route to Mileage screen
          navigateTo(`/d/company/${businessEntityUuid}/money/mileage`);
          break;
        case 'Hurdlr.CASH_FLOW_TILE':
        case 'Hurdlr.PROFIT_LOSS_TILE': // route to Reports screen
          navigateTo(`/d/company/${businessEntityUuid}/money/reports`);
          break;
        default:
          return false;
      }
    }
    return false;
  };
}

export function configureZbMoneyPlaidListenerCallback(businessEntityUuid, unlinkHurdlrPlaid) {
  return ({ type, plaidAccessToken, accessToken }) => {
    // Plaid
    switch (type) {
      case 'Hurdlr.PLAID_LINK_UNLINK':
        unlinkHurdlrPlaid({ variables: { plaidAccessToken } });
        break;
      case 'Hurdlr.PLAID_LINK_RESET':
        window.localStorage.setItem('hurdlrType', type);
        if (isUndefined(plaidAccessToken)) {
          window.localStorage.setItem('plaidAccessToken', accessToken);
        } else {
          window.localStorage.setItem('plaidAccessToken', plaidAccessToken);
        }
        navigateToZbPlaidFlow(businessEntityUuid);
        break;
      default:
        window.localStorage.setItem('hurdlrType', type);
        window.localStorage.setItem('plaidAccessToken', plaidAccessToken);
        navigateToZbPlaidFlow(businessEntityUuid);
    }
  };
}

export function configureZbMoneyInvoiceCallback({ businessEntityUuid, refundInvoice }) {
  const gotoInvoiceHome = () => navigateTo(`/d/company/${businessEntityUuid}/money/invoices`);
  return ({ type, invoiceId }) => {
    switch (type) {
      case 'Hurdlr.SETUP_PAYMENT_PROCESSING':
      // route user to my existing payment processing setup flow
      // once complete, POST to /invoiceSetup to set
      // { invoiceSetupComplete: true, thirdPartyPaymentsEnabled: true }
        navigateTo(buildCartUrl('zb_money_invoice_setup', { businessEntityUuid }));
        break;
      case 'Hurdlr.REFUND_INVOICE':
        if (refundInvoice) {
          const id = Number.parseInt(invoiceId, 10);
          refundInvoice(id);
        }
        break;
      default:
        return gotoInvoiceHome;
    }
    return gotoInvoiceHome;
  };
}

const defaultText = {
  header: {
    title: 'Run your business. Rule your finances.',
    description: 'ZenBusiness Money has all the tools you need to find your financial flow.'
  },
  hurdlr: {
    default: {
      header: 'Real numbers in real time'
    },
    linked: {
      description: 'Tap a card for a closer look'
    },
    unlinked: {
      description: 'Link your bank acccount to view your expenses and deductions.'
    },
    notSupported: {
      text1: 'Good news. Soon you can access ZenBusiness Money Essential and Pro on your mobile device. But not yet.',
      text2: 'For now,',
      text3: ' keep using your computer to access your account. ',
      text4: 'We’ll let you know when it’s ready for mobile devices.'
    }
  }
};

export const MONEY_DASH_COPY_DICT = {
  none: defaultText,
  shared: {
    linkAccount: 'Link my account',
    unlockPayments: {
      row1: {
        header: 'Accounting and easy tax management',
        subHeader: `Unlock the rest of your account’s tools for just $30/month.
        Add income tracking and tax management, plus more insights
        to help you run business.`
      },
      row2: {
        header: 'Make it easy to get paid fast',
        subHeader: 'Unlock Payments to accept credit cards or bank transfer (ACH). Get paid fast, give great service.'
      }
    }
  },
  zb_money_essential: {
    ...defaultText
  },
  zb_money_pro: defaultText
};

export const MONEY_PRO_LANDING_PAGE = {
  topSection: {
    title: 'Money Pro',
    subtitle: 'One tool for expenses, invoices, payments, and taxes.'
  },
  topCTA: {
    text1: 'Free for 30 days!',
    text2: 'You may cancel the free trial any time within 30 days.'
  },
  monthlyButton: '$30/month',
  yearlyButtom: '$300/year',
  tryButton: 'Try for free',
  carouselBackground: {
    lg: '/s2/vector/mp-lp-carousel-background-lg.svg',
    xs: '/s2/vector/mp-lp-carousel-background-xs.svg'
  },
  carouselImages: [
    {
      url: {
        lg: '/s2/raster/mp-lp-carousel-a-overview-lg.png',
        xs: '/s2/raster/mp-lp-carousel-a-overview-xs.png'
      },
      alt: 'Carousel Overview page',
      caption: 'See how your business finances are performing with the Money Pro dashboard.'
    },
    {
      url: {
        lg: '/s2/raster/mp-lp-carousel-b-invoices-lg.png',
        xs: '/s2/raster/mp-lp-carousel-b-invoices-xs.png'
      },
      alt: 'Carousel Invoices page',
      caption: 'Keep track of when invoices are paid or past due. You can also send payment reminders.'
    },
    {
      url: {
        lg: '/s2/raster/mp-lp-carousel-c-expenses-lg.png',
        xs: '/s2/raster/mp-lp-carousel-c-expenses-xs.png'
      },
      alt: 'Carousel Expenses page',
      caption: 'Track expenses to know exactly how much you spend each month.'
    },
    {
      url: {
        lg: '/s2/raster/mp-lp-carousel-d-taxes-lg.png',
        xs: '/s2/raster/mp-lp-carousel-d-taxes-xs.png'
      },
      alt: 'Carousel Taxes page',
      caption: 'See what you might owe in taxes or get back as a refund.'
    },
    {
      url: {
        lg: '/s2/raster/mp-lp-carousel-e-income-lg.png',
        xs: '/s2/raster/mp-lp-carousel-e-income-xs.png'
      },
      alt: 'Carousel Income page',
      caption: 'Track your income to know how much money is coming in.'
    },
    {
      url: {
        lg: '/s2/raster/mp-lp-carousel-f-mileage-lg.png',
        xs: '/s2/raster/mp-lp-carousel-f-mileage-xs.png'
      },
      alt: 'Carousel Milage page',
      caption: 'Log your business trips to maximize your tax deductions.'
    },
    {
      url: {
        lg: '/s2/raster/mp-lp-carousel-g-reports-lg.png',
        xs: '/s2/raster/mp-lp-carousel-g-reports-xs.png'
      },
      alt: 'Carousel Reports page',
      caption: 'View, download, or email different financial reports for valuable insights.'
    }
  ],
  terms: [
    {
      imgUrl: '/s2/vector/calendar-and-dollar-sign.svg',
      title: 'Try it free for 30 days',
      description: 'Use Money Pro\'s tools for free. After 30 days, it\'s only $30/month.'
    },
    {
      imgUrl: '/s2/vector/free-trial-time.svg',
      title: 'Cancel any time',
      description: 'It\'s easy to cancel your free trial. Go online or call customer support.'
    },
    {
      imgUrl: '/illustration/tax-savings-consult-real-accountants.svg',
      title: 'Keep using Pro',
      description: 'When your free trial is over, we\'ll automatically start your paid plan.'
    }
  ],
  proFeatureTitle: 'Money Pro Features',
  proFeatures: [
    {
      pill: 'Invoices',
      title: 'Send pay-enabled invoices',
      text: 'Create custom invoices with your logo that can be paid by card or ACH.',
      imgUrl: '/s2/vector/mp-lp-feature-expression-invoices.svg'
    },
    {
      pill: 'Expenses',
      title: 'Tag expenses for deductions',
      text: 'Quickly flag business expenses for easy bookkeeping and tax savings.',
      imgUrl: '/s2/vector/mp-lp-feature-expression-expenses.svg'
    },
    {
      pill: 'Taxes',
      title: 'Tax prep and filing help',
      text: 'Get free tax filing help, so you can be confident your taxes are filed correctly.',
      imgUrl: '/s2/vector/mp-lp-feature-expression-taxes.svg'
    },
    {
      pill: 'Income',
      title: 'Track income for insights',
      text: 'Keep an eye on your revenue to help steer smart financial decisions.',
      imgUrl: '/s2/vector/mp-lp-feature-expression-income.svg'
    },
    {
      pill: 'Reports',
      title: 'Easy-to-read reports',
      text: 'Simple reports for complex data help you measure and track growth.',
      imgUrl: '/s2/vector/mp-lp-feature-expression-reports.svg'
    },
    {
      pill: 'Mileage Tracking',
      title: 'Track and log mileage',
      text: 'Log work miles in one place and easily generate mileage reports.',
      imgUrl: '/s2/vector/mp-lp-feature-expression-mileage-tracking.svg'
    }
  ],
  bottomCTA: {
    imgUrl: '/s2/animation/lottie/money-bounce.json',
    title: 'Money Pro',
    text1: 'Free for 30 days.',
    text2: 'Free for 30 days, then $30/month.'
  }
};
